import {Injectable, signal, computed} from '@angular/core';

export interface ImpersonatedUser {
  name: string;
  email: string;
  roles: string[];
  organization: string;
}

/**
 * Keycloak liefert den organization-Claim je nach Zuweisungsart unterschiedlich:
 * - Legacy-Attribut: String oder String[] (z.B. "FreyMuT" / ["FreyMuT"])
 * - native Keycloak-Organizations-Mitgliedschaft: Objekt keyed by Org-Name
 *   (z.B. { "FreyMuT": {} }) — hier muss der Key extrahiert werden.
 */
export function normalizeOrganizationClaim(raw: unknown): string {
    if (typeof raw === 'string') return raw;
    if (Array.isArray(raw)) return typeof raw[0] === 'string' ? raw[0] : '';
    if (raw && typeof raw === 'object') return Object.keys(raw)[0] ?? '';
    return '';
}

function organizationFromToken(token: string): string {
    try {
        const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
        return normalizeOrganizationClaim(JSON.parse(atob(padded)).organization);
    } catch {
        return '';
    }
}

@Injectable({providedIn: 'root'})
export class ImpersonationService {
  private readonly _token = signal<string | null>(null);
  private readonly _user = signal<ImpersonatedUser | null>(null);

  readonly isImpersonating = computed(() => this._token() !== null);
  readonly impersonatedUser = this._user.asReadonly();

  setImpersonation(token: string, user: Omit<ImpersonatedUser, 'organization'> & {organization: unknown}): void {
    // Ohne Organisation sendet der Interceptor keinen X-Tenant-ID-Header und das
    // Backend fällt auf die Subdomain als Tenant zurück — tenant-gescopte Daten
    // (Kurse, Zertifikat-Vorlagen, …) kommen dann aus dem falschen Tenant.
    // Ticket-Wert normalisieren, sonst auf den Claim im Token selbst ausweichen.
    const organization = normalizeOrganizationClaim(user.organization) || organizationFromToken(token);
    this._token.set(token);
    this._user.set({...user, organization});
  }

  clearImpersonation(): void {
    this._token.set(null);
    this._user.set(null);
  }

  getToken(): string | null {
    return this._token();
  }
}
